import {
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  ORDER,
  removeEmptyFilters,
  setFocusedOrderID,
  useAccordionFilterBuilder,
  useGetNewEntityWithTranslatedTextPipe,
  usePersistedBlotterTable,
  useWLOrderFormDispatch,
  useWsBlotterTable,
  type CustomerOrder,
  type OrdStatusEnum,
  type SideEnum,
  type WebsocketRequest,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo } from 'react';

import { useOMSStateContext } from '@src/providers/OMSStateProvider';

import { onlyServerFilterKeys, useOrdersFilter } from '@src/components/Filters/FilterBuilder/useOrdersFilter';
import type { CellClickedEvent } from 'ag-grid-community';
import { useOrderHistoryBlotterColumns } from './useOrderHistoryBlotterColumns';

interface OrderHistoryWebsocketRequest extends WebsocketRequest {
  MarketAccounts?: string[];
  Sides?: SideEnum[];
  Statuses?: OrdStatusEnum[];
  Strategies?: string[];
  Symbols?: string[];
}

interface UseOrderHistoryBlotterProps {
  maxRows: number;
  showOnlySelfOrders?: boolean;
}

const INITIAL_FILTER = { StartDate: Sugar.Date.create('a year ago') };

const DEFAULT_SORT = '-SubmitTime';

export const useOrderHistoryBlotter = ({ maxRows, showOnlySelfOrders = false }: UseOrderHistoryBlotterProps) => {
  const { selectedClOrdID } = useOMSStateContext();

  const columns = useOrderHistoryBlotterColumns();
  const persistedBlotterTable = usePersistedBlotterTable(`orderHistory`, {
    filter: INITIAL_FILTER,
  });

  const filteredOrders = useOrdersFilter({ persistedBlotterTable, showOnlySelfOrders });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filteredOrders;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });
  const orderFormDispatch = useWLOrderFormDispatch();
  const handleOnCellClicked = useCallback(
    (event: CellClickedEvent<CustomerOrder, any>) => {
      if (event.data != null) {
        orderFormDispatch(setFocusedOrderID(event.data.OrderID));
      }
    },
    [orderFormDispatch]
  );

  const request = useMemo<OrderHistoryWebsocketRequest>(
    () => ({
      name: ORDER,
      tag: 'ORDER_HISTORY',
      sort_by: DEFAULT_SORT,
      ...removeEmptyFilters(onlyServerFilterKeys(filteredOrders.filter)),
    }),
    [filteredOrders.filter]
  );

  const translateCustomerOrderTextPipe = useGetNewEntityWithTranslatedTextPipe<CustomerOrder>();
  const blotterTable = useWsBlotterTable<WebsocketRequest, CustomerOrder>({
    request,
    columns,
    persistence: persistedBlotterTable,
    pipe: translateCustomerOrderTextPipe,
    rowID: 'OrderID',
    clientLocalFilter,
    flashRows: ['add'],
    startingRowLimit: maxRows,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
      rowHeight: 32,
      onCellClicked: handleOnCellClicked,
    },
  });

  useEffect(() => {
    for (const blotterRow of blotterTable.getRows()) {
      blotterRow.setSelected(blotterRow.data.ClOrdID === selectedClOrdID);
    }
  }, [blotterTable, selectedClOrdID]);

  return useMemo(
    () => ({
      blotterTable,
      blotterTableFilterProps,
      filterBuilderAccordion,
    }),
    [blotterTable, blotterTableFilterProps, filterBuilderAccordion]
  );
};
